(() => {
  // Keep visit-level deduplication when the theme editor reloads a section.
  if (window.UGJPTracking) {
    window.UGJPTracking.refresh();
    return;
  }

  const eventNames = new Set([
    'jp_sp_click', 'jp_sp_scroll', 'jp_sp_view', 'jp_sp_menu_open',
    'jp_sp_tab_change', 'jp_sp_modal_view', 'jp_sp_video_start',
    'jp_sp_video_progress', 'jp_sp_video_complete',
  ]);
  const fields = [
    'page_id', 'module_id', 'section_id', 'scene_id', 'series_id',
    'series_block_id', 'from_series_id', 'button_id', 'menu_id',
    'target_module', 'target_anchor', 'product_id', 'hotspot_slot',
    'destination', 'destination_url', 'video_id', 'video_provider',
    'scroll_percent', 'video_percent', 'view_type',
  ];
  const numericFields = new Set(['scroll_percent', 'video_percent']);
  const thresholds = [25, 50, 75, 90];
  const measurementId = 'G-XSMFFJQP5R';
  const eventSuffix = '_3c';
  const pageId = 'jp_spokesperson';
  const sent = new Set();
  const dwell = new Map();
  const events = [];
  let root;
  let configNode;
  let configText;
  let config = {};
  let targets = [];
  let permitted = false;
  let initialized = false;
  let consentLoading = false;
  let consentAttempts = 0;
  let nextConsentAttempt = 0;
  let observer;
  let logging = false;
  let lastStatus = '';
  let lastFailure = '';
  try {
    logging = new URL(window.location.href).searchParams.get('jp_tracking_log') === '1';
  } catch { /* Diagnostics must never affect tracking. */ }

  function print(label, value) {
    try { console.info('[JP Tracking] ' + label, value); } catch { /* Console can be unavailable. */ }
  }

  // Read-only: unlike allowed(), this does not update consent or deduplication state.
  function status() {
    let analyticsAllowed = null;
    let consentError = false;
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.analyticsProcessingAllowed === 'function') {
        analyticsAllowed = privacy.analyticsProcessingAllowed() === true;
      }
    } catch { consentError = true; }
    const designMode = Boolean(config.designMode || window.Shopify?.designMode);
    const senderAvailable = typeof window.gtag === 'function';
    let reason = 'ready';
    if (!root?.isConnected) reason = 'page_missing';
    else if (!configNode?.isConnected) reason = 'config_missing';
    else if (!config.enabled) reason = 'disabled';
    else if (config.debug) reason = 'local_only';
    else if (designMode) reason = 'theme_editor';
    else if (!liveSite()) reason = 'wrong_host';
    else if (consentError) reason = 'consent_api_error';
    else if (analyticsAllowed === null) reason = 'consent_api_missing';
    else if (!analyticsAllowed) reason = 'analytics_not_allowed';
    else if (!senderAvailable) reason = 'sender_missing';
    const messages = {
      ready: '允许交给现有发送接口；是否到达 GA4 仍需验证',
      page_missing: '未找到代言人页面容器，请检查页面模板',
      config_missing: '未找到公共资源中的埋点配置',
      disabled: '页面埋点开关已关闭',
      local_only: '仅本地调试：只记录日志，不发送 GA4',
      theme_editor: '主题编辑器内不上报，请打开店铺独立预览页',
      wrong_host: '当前域名不符合此站点的上报条件',
      consent_api_error: '读取 Cookie 权限失败，暂不上报',
      consent_api_missing: 'Cookie 权限接口未就绪，暂不上报',
      analytics_not_allowed: '统计 Cookie 未获允许，不记录或发送本次行为',
      sender_missing: '当前页面发送接口未就绪，无法交付事件',
    };
    return {
      page_id: pageId, event_suffix: eventSuffix,
      logging, mode: config.debug ? 'local_only' : 'production',
      enabled: config.enabled === true, design_mode: designMode,
      hostname: window.location.hostname, analytics_allowed: analyticsAllowed,
      transport: 'gtag', transport_available: senderAvailable,
      data_layer_available: typeof window.dataLayer?.push === 'function',
      reason, message: messages[reason], ga4_delivery_verified: false,
    };
  }

  function reportStatus(force = false) {
    if (!logging) return;
    const current = status();
    const key = JSON.stringify(current);
    if (force || key !== lastStatus) print('状态', current);
    lastStatus = key;
  }

  function setLogging(value = true) {
    logging = value === true;
    lastStatus = '';
    lastFailure = '';
    reportStatus(true);
    return status();
  }

  function recordEvent(name, payload) {
    if (!logging && !config.debug) return;
    const entry = { event: name, ...payload };
    events.push(entry);
    if (events.length > 200) events.shift();
    print(config.debug ? 'local_only（不上报）' : 'queued（已交给发送接口，非 GA4 接收确认）', entry);
  }

  function reportFailure(name, reason) {
    if (!logging) return;
    const key = name + ':' + reason;
    if (key !== lastFailure) print('未交付', { event: name, reason });
    lastFailure = key;
  }

  function readConfig() {
    root = document.getElementById('ug-jp-spokesperson-page');
    configNode = root?.querySelector('[data-jp-tracking-config]');
    const value = configNode?.textContent || '';
    if (value === configText) return;
    configText = value;
    try {
      const next = JSON.parse(value);
      config = {
        enabled: next.enabled === true,
        debug: next.debug === true,
        designMode: next.designMode === true,
        viewThreshold: Math.max(10, Math.min(100, Number(next.viewThreshold) || 50)) / 100,
        viewDuration: Math.max(500, Math.min(3000, Number(next.viewDuration) || 1000)),
      };
    } catch {
      config = {};
    }
    dwell.clear();
  }

  function eligible() {
    return Boolean(root?.isConnected && configNode?.isConnected && config.enabled);
  }

  function liveSite() {
    return !config.designMode && !window.Shopify?.designMode &&
      /^(www\.)?ugreen\.jp$/i.test(window.location.hostname);
  }

  function hasPermission() {
    if (!eligible()) return false;
    if (config.debug) return true;
    if (!liveSite()) return false;
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch {
      return false;
    }
  }

  function depth() {
    const height = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight || 0);
    return height > 0 ? Math.min(100, (window.scrollY + window.innerHeight) / height * 100) : 0;
  }

  function updatePermission() {
    reportStatus();
    const next = hasPermission();
    if (next === permitted) return next;
    permitted = next;
    dwell.clear();
    // Consent granted later must not replay scrolling that happened without consent.
    if (next && initialized) {
      const current = depth();
      thresholds.forEach((percent) => {
        if (current >= percent) sent.add('scroll:' + percent);
      });
    }
    document.dispatchEvent(new CustomEvent('jp:tracking-consent', { detail: { allowed: next } }));
    return next;
  }

  function loadConsent() {
    if (!eligible() || config.debug || !liveSite() || window.Shopify?.customerPrivacy) return;
    if (consentLoading || consentAttempts >= 3 || Date.now() < nextConsentAttempt) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') return;
    consentLoading = true;
    consentAttempts += 1;
    nextConsentAttempt = Date.now() + 5000;
    // Use the site's existing consent decisions. Never set or request consent here.
    const timeout = setTimeout(() => { consentLoading = false; }, 10000);
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
        clearTimeout(timeout);
        consentLoading = false;
        updatePermission();
      });
    } catch {
      clearTimeout(timeout);
      consentLoading = false;
    }
  }

  function cleanString(value) {
    return typeof value === 'string' || typeof value === 'number'
      ? String(value).replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 100)
      : '';
  }

  function cleanURL(value) {
    if (!value) return '';
    try {
      const url = new URL(String(value), window.location.href);
      if (!/^https?:$/.test(url.protocol)) return '';
      // Queries, fragments and user info can carry personal or campaign form data.
      return (url.origin + url.pathname).slice(0, 100);
    } catch {
      return '';
    }
  }

  function track(name, params = {}, onceKey) {
    if (!eventNames.has(name) || !updatePermission() || (onceKey && sent.has(onceKey))) return false;
    const emittedName = name + eventSuffix;
    // Clear unrelated fields, including any defaults configured on the Google tag.
    const payload = {};
    fields.forEach((field) => {
      const value = params[field];
      if (field === 'destination_url') payload[field] = cleanURL(value);
      else if (numericFields.has(field)) {
        payload[field] = value !== '' && value != null && Number.isFinite(Number(value))
          ? Math.max(0, Math.min(100, Number(value))) : '';
      } else payload[field] = cleanString(value);
    });
    payload.page_id = pageId;
    if (!config.debug) {
      if (typeof window.gtag !== 'function') {
        reportFailure(emittedName, 'sender_missing');
        return false;
      }
      try {
        window.gtag('event', emittedName, { ...payload, send_to: measurementId });
      } catch {
        reportFailure(emittedName, 'sender_error');
        return false;
      }
    }
    lastFailure = '';
    recordEvent(emittedName, payload);
    if (onceKey) sent.add(onceKey);
    if (name === 'jp_sp_modal_view') dwell.clear();
    return true;
  }

  function context(element) {
    const result = {};
    if (!element || !root?.contains(element)) return result;
    for (let node = element; node && node !== root.parentElement; node = node.parentElement) {
      fields.forEach((field) => {
        const name = 'data-jp-' + field.replace(/_/g, '-');
        if (!(field in result) && node.hasAttribute(name)) result[field] = node.getAttribute(name);
      });
    }
    return result;
  }

  function visible(element, viewport = false) {
    if (!element.isConnected || element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
    for (let node = element; node && node !== root?.parentElement; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    if (!viewport) return true;
    const width = document.documentElement.clientWidth || window.innerWidth;
    const height = window.innerHeight;
    const visibleWidth = Math.max(0, Math.min(rect.right, width) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, height) - Math.max(rect.top, 0));
    // A long module uses the viewport as the maximum observable area.
    return visibleWidth * visibleHeight / (Math.min(rect.width, width) * Math.min(rect.height, height)) >= config.viewThreshold;
  }

  function clicked(event) {
    if (!config.debug && !event.isTrusted) return;
    if (event.type === 'auxclick' ? event.button !== 1 : event.button !== 0) return;
    const target = event.target instanceof Element ? event.target.closest('[data-jp-track-click]') : null;
    if (!target || !root?.contains(target) || !visible(target) || target.closest('[disabled], [aria-disabled="true"]')) return;
    const params = context(target);
    if (target.tagName === 'A') {
      const href = target.getAttribute('href')?.trim();
      if (!href || href.startsWith('#') || !cleanURL(href)) return;
      params.destination_url = href;
    } else if (target.tagName !== 'BUTTON') return;
    track('jp_sp_click', params);
  }

  function refresh() {
    readConfig();
    targets = root ? Array.from(root.querySelectorAll('[data-jp-view]')) : [];
    for (const target of dwell.keys()) if (!targets.includes(target)) dwell.delete(target);
    updatePermission();
    loadConsent();
  }

  function tick() {
    if (!root?.isConnected || !configNode?.isConnected) refresh();
    loadConsent();
    if (!updatePermission() || document.visibilityState !== 'visible') {
      dwell.clear();
      return;
    }
    const percent = depth();
    thresholds.forEach((threshold) => {
      if (percent >= threshold) track('jp_sp_scroll', { scroll_percent: threshold }, 'scroll:' + threshold);
    });
    const overlay = document.querySelector('dialog[open], [data-ug-jpcm-modal].is-open');
    const now = performance.now();
    targets.forEach((target) => {
      const type = target.getAttribute('data-jp-view');
      if (!['module', 'scene', 'series'].includes(type)) return;
      const params = { ...context(target), view_type: type };
      const key = ['view', type, params.module_id, params.section_id, params.scene_id, params.series_id, params.series_block_id].join(':');
      if (sent.has(key)) return;
      if ((overlay && !overlay.contains(target)) || !visible(target, true)) {
        dwell.delete(target);
        return;
      }
      if (!dwell.has(target)) dwell.set(target, now);
      if (now - dwell.get(target) >= config.viewDuration && track('jp_sp_view', params, key)) dwell.delete(target);
    });
  }

  window.UGJPTracking = {
    track, context, refresh, allowed: updatePermission, status, setLogging,
    get debug() { return config.debug === true; },
    // Bounded history only when local debugging or explicit Console logging is enabled.
    events,
  };

  function boot() {
    refresh();
    initialized = true;
    document.addEventListener('click', clicked, true);
    document.addEventListener('auxclick', clicked, true);
    document.addEventListener('visitorConsentCollected', () => {
      consentAttempts = 0;
      refresh();
    });
    document.addEventListener('visibilitychange', () => { dwell.clear(); });
    window.addEventListener('scroll', () => {
      for (const target of dwell.keys()) if (!visible(target, true)) dwell.delete(target);
    }, { passive: true });
    window.addEventListener('resize', () => { dwell.clear(); }, { passive: true });
    document.addEventListener('shopify:section:load', refresh);
    document.addEventListener('shopify:section:unload', () => { queueMicrotask(refresh); });
    // Child/config updates include editor replacement and lazy-inserted sections.
    observer = new MutationObserver((changes) => {
      changes.forEach((change) => {
        if (change.type !== 'attributes') return;
        if (change.attributeName === 'open') dwell.clear();
        else for (const target of dwell.keys()) {
          if (change.target.contains(target)) dwell.delete(target);
        }
      });
      if (changes.some((change) => change.type === 'childList' || configNode?.contains(change.target))) refresh();
    });
    observer.observe(document.body, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'inert', 'open'],
    });
    setInterval(tick, 200);
    tick();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
